// Code Interpreter sandbox — runs Python from chat code blocks in the
// user's browser via Pyodide (CPython compiled to WebAssembly).
//
// Why client-side: untrusted model-written code never touches the server.
// The interpreter lives in a dedicated Web Worker (no DOM, no page
// storage, no auth token) whose network access is locked to the Pyodide
// CDN and PyPI (for pure-Python package installs). A runaway script is
// stopped by terminating the worker.
//
// The pure helpers at the top are exported for node:test; everything
// that needs `Worker` is only touched from the browser.

export const PYODIDE_VERSION = "0.29.5";
export const PYODIDE_BASE = `https://cdn.jsdelivr.net/pyodide/v${PYODIDE_VERSION}/full/`;
export const SANDBOX_ALLOWED_PREFIXES = [
  PYODIDE_BASE,
  "https://pypi.org/",
  "https://files.pythonhosted.org/",
];
export const RUN_TIMEOUT_MS = 60_000;
export const LOAD_TIMEOUT_MS = 180_000;
export const MAX_OUTPUT_CHARS = 20_000;
export const MAX_SANDBOX_FILES = 20;
export const MAX_SANDBOX_FILE_BYTES = 50 * 1024 * 1024;

// Pure-Python packages that are not in the Pyodide distribution but are
// commonly imported by data-analysis code. Installed on demand via
// micropip from PyPI.
export const MICROPIP_PACKAGES = {
  openpyxl: "openpyxl",
  seaborn: "seaborn",
  tabulate: "tabulate",
  xlrd: "xlrd",
};

export function isPythonLang(lang) {
  const l = String(lang || "").toLowerCase().trim();
  return l === "python" || l === "py" || l === "python3";
}

export function isAllowedSandboxUrl(url) {
  if (typeof url !== "string" || !url) return false;
  return SANDBOX_ALLOWED_PREFIXES.some((p) => url.startsWith(p));
}

// Keep names flat and boring so a file can't be written outside /data.
export function sanitizeSandboxFilename(name) {
  const base = String(name || "file").split(/[\\/]/).pop() || "file";
  const cleaned = base.replace(/[^\w.\- ]+/g, "_").replace(/^\.+/, "").trim();
  return (cleaned || "file").slice(0, 120);
}

// Extra pure-Python packages the code will need. Imports are matched
// literally; pandas.read_excel needs openpyxl even with no import line.
export function extraPackagesFor(code) {
  const src = String(code || "");
  const need = new Set();
  for (const mod of Object.keys(MICROPIP_PACKAGES)) {
    const re = new RegExp(`^\\s*(?:import|from)\\s+${mod}\\b`, "m");
    if (re.test(src)) need.add(MICROPIP_PACKAGES[mod]);
  }
  if (/read_excel|to_excel|\.xlsx\b/.test(src)) need.add("openpyxl");
  return [...need];
}

// Fenced python blocks in a markdown message, in order.
export function extractPythonBlocks(text) {
  const out = [];
  const re = /```([\w+-]*)[^\n]*\n([\s\S]*?)```/g;
  let m;
  while ((m = re.exec(String(text || "")))) {
    if (isPythonLang(m[1])) out.push(m[2].replace(/\n$/, ""));
  }
  return out;
}

export function truncateOutput(s, max = MAX_OUTPUT_CHARS) {
  const str = String(s || "");
  if (str.length <= max) return str;
  return str.slice(0, max) + `\n…[truncated, ${(str.length - max).toLocaleString()} more chars]`;
}

// Markdown handed back to the model when the user sends the output to chat.
export function formatRunForChat(result, { maxChars = 6000 } = {}) {
  if (!result) return "";
  const parts = ["Here is the output from running that code:"];
  const text = [result.stdout, result.stderr].filter(Boolean).join("\n").trim();
  if (text) parts.push("```text\n" + truncateOutput(text, maxChars) + "\n```");
  if (result.resultRepr) parts.push("Result:\n```text\n" + truncateOutput(result.resultRepr, 2000) + "\n```");
  if (result.table) parts.push(`(A table with ${result.table.totalRows} rows × ${result.table.columns.length} columns was displayed.)`);
  if (result.images && result.images.length) parts.push(`(${result.images.length} chart${result.images.length === 1 ? "" : "s"} generated.)`);
  if (result.error) parts.push("It raised an error:\n```text\n" + truncateOutput(result.error, 3000) + "\n```");
  if (parts.length === 1) parts.push("(The code ran with no output.)");
  return parts.join("\n\n");
}

export function codeInterpreterSystemPrompt(fileNames = []) {
  const files = fileNames.length
    ? `\nFiles the user attached are available in the working directory /data: ${fileNames.map((n) => "`" + n + "`").join(", ")}.`
    : "";
  return "\n\n# Code Interpreter\n" +
    "You can run Python. Write it in ```python fenced blocks; each block runs in a sandboxed Python 3.13 (Pyodide) in the user's browser and the user sees stdout, the value of the last expression, tables and charts." +
    " numpy, pandas, matplotlib, scipy, scikit-learn and statsmodels are available; openpyxl and seaborn install automatically when imported. There is NO network access — never fetch URLs." +
    " Variables persist between blocks in the same session. For charts use matplotlib (they are captured automatically; plt.show() is optional). End a block with a DataFrame expression to show it as a table." +
    files +
    "\nKeep each block self-contained enough to run on its own, and explain the results after the code.";
}

// ---------------------------------------------------------------------------
// Worker source. Kept as a string so the sandbox ships with the app bundle
// and is instantiated from a Blob URL (no extra route, no build plugin).
// ---------------------------------------------------------------------------
export function buildWorkerSource() {
  return `
"use strict";
const BASE = ${JSON.stringify(PYODIDE_BASE)};
const ALLOWED = ${JSON.stringify(SANDBOX_ALLOWED_PREFIXES)};
const MICROPIP_OK = ${JSON.stringify(Object.values(MICROPIP_PACKAGES))};
function allowed(u) { try { const abs = new URL(String(u), BASE).href; return ALLOWED.some(p => abs.startsWith(p)); } catch (e) { return false; } }
const realFetch = self.fetch.bind(self);
self.fetch = function (input, init) {
  const url = typeof input === "string" ? input : (input && input.url) || "";
  if (!allowed(url)) return Promise.reject(new TypeError("Network access is disabled in the Code Interpreter sandbox"));
  return realFetch(input, init);
};
if (self.XMLHttpRequest) {
  const realOpen = self.XMLHttpRequest.prototype.open;
  self.XMLHttpRequest.prototype.open = function (method, url) {
    if (!allowed(url)) throw new TypeError("Network access is disabled in the Code Interpreter sandbox");
    return realOpen.apply(this, arguments);
  };
}
try { self.WebSocket = undefined; } catch (e) {}
try { self.EventSource = undefined; } catch (e) {}
try { self.importScripts(BASE + "pyodide.js"); } catch (e) { self.__cfLoadError = String(e && e.message || e); }

let py = null, ns = null, readyP = null;
const SETUP = [
  "import os, io, base64, warnings",
  "os.environ['MPLBACKEND'] = 'AGG'",
  "os.makedirs('/data', exist_ok=True)",
  "os.chdir('/data')",
  "warnings.filterwarnings('ignore', message='.*non-interactive.*')",
  "warnings.filterwarnings('ignore', message='.*Matplotlib is currently using agg.*')",
  "def _cf_capture_figs():",
  "    import sys",
  "    if 'matplotlib.pyplot' not in sys.modules: return []",
  "    plt = sys.modules['matplotlib.pyplot']",
  "    out = []",
  "    for num in plt.get_fignums():",
  "        fig = plt.figure(num)",
  "        buf = io.BytesIO()",
  "        fig.savefig(buf, format='png', dpi=110, bbox_inches='tight')",
  "        out.append(base64.b64encode(buf.getvalue()).decode('ascii'))",
  "    plt.close('all')",
  "    return out",
  "def _cf_table(v):",
  "    try:",
  "        import pandas as pd",
  "    except Exception:",
  "        return None",
  "    if isinstance(v, pd.Series): v = v.to_frame()",
  "    if not isinstance(v, pd.DataFrame): return None",
  "    head = v.head(200)",
  "    return {'columns': [str(c) for c in head.columns], 'index': [str(i) for i in head.index], 'rows': [[None if x is None else str(x) for x in r] for r in head.astype(object).where(head.notna(), None).values.tolist()], 'totalRows': int(len(v))}",
].join("\\n");

function post(msg) { self.postMessage(msg); }

function ensureReady(id) {
  if (readyP) return readyP;
  readyP = (async () => {
    if (self.__cfLoadError || typeof self.loadPyodide !== "function") throw new Error("Couldn't load the Python runtime: " + (self.__cfLoadError || "loader missing"));
    post({ id, type: "status", status: "Starting Python…" });
    py = await self.loadPyodide({ indexURL: BASE });
    py.runPython(SETUP);
    ns = py.globals;
  })();
  readyP.catch(() => { readyP = null; });
  return readyP;
}

self.onmessage = async (e) => {
  const { id, type, code, files, extraPackages } = e.data || {};
  if (type === "reset") { try { if (py) { py.runPython("for _k in [k for k in list(globals()) if not k.startswith('_') and k not in ('os','io','base64','warnings')]: del globals()[_k]"); } } catch (err) {} post({ id, type: "reset_done" }); return; }
  if (type !== "run") return;
  const stdout = [], stderr = [];
  const t0 = Date.now();
  try {
    await ensureReady(id);
    for (const f of files || []) {
      try { py.FS.writeFile("/data/" + f.name, new Uint8Array(f.bytes)); } catch (err) { stderr.push("Could not stage " + f.name + ": " + err.message); }
    }
    py.setStdout({ batched: (s) => stdout.push(s) });
    py.setStderr({ batched: (s) => stderr.push(s) });
    post({ id, type: "status", status: "Loading packages…" });
    await py.loadPackagesFromImports(code, { messageCallback: () => {}, errorCallback: (m) => stderr.push(String(m)) });
    const extras = (extraPackages || []).filter((p) => MICROPIP_OK.includes(p));
    if (extras.length) {
      post({ id, type: "status", status: "Installing " + extras.join(", ") + "…" });
      await py.loadPackage("micropip", { messageCallback: () => {} });
      const micropip = py.pyimport("micropip");
      await micropip.install(extras);
    }
    post({ id, type: "status", status: "Running…", runStarted: true });
    const value = await py.runPythonAsync(code, { globals: ns });
    let resultRepr = null, table = null;
    if (value !== undefined && value !== null) {
      if (py.ffi && value instanceof py.ffi.PyProxy) {
        const t = ns.get("_cf_table")(value);
        if (t) { table = t.toJs({ dict_converter: Object.fromEntries }); t.destroy && t.destroy(); }
        else resultRepr = py.globals.get("repr")(value);
        value.destroy && value.destroy();
      } else {
        resultRepr = String(value);
      }
    }
    const imgsProxy = ns.get("_cf_capture_figs")();
    const images = imgsProxy.toJs(); imgsProxy.destroy && imgsProxy.destroy();
    post({ id, type: "result", ok: true, stdout: stdout.join("\\n"), stderr: stderr.join("\\n"), resultRepr, table, images, durationMs: Date.now() - t0 });
  } catch (err) {
    let images = [];
    try { const p = ns && ns.get("_cf_capture_figs")(); if (p) { images = p.toJs(); p.destroy && p.destroy(); } } catch (e2) {}
    post({ id, type: "result", ok: false, stdout: stdout.join("\\n"), stderr: stderr.join("\\n"), error: String((err && err.message) || err), images, durationMs: Date.now() - t0 });
  }
};
`;
}

// ---------------------------------------------------------------------------
// Browser-only runtime: one warm worker, a file registry fed by the chat
// composer, and a small result store keyed by code so a re-rendered code
// block keeps its output.
// ---------------------------------------------------------------------------
let _worker = null;
let _workerUrl = null;
let _seq = 0;
const _pending = new Map(); // id -> { resolve, onStatus, timer, runTimer }
const _files = new Map(); // sanitized name -> File/Blob
const _results = new Map(); // code -> { state, status, result }
const _listeners = new Set();
const _autoRun = new Set();

function _emit() { for (const fn of _listeners) { try { fn(); } catch {} } }

export function subscribeRuns(fn) { _listeners.add(fn); return () => _listeners.delete(fn); }
export function getRunState(code) { return _results.get(code) || null; }

export function registerSandboxFile(file) {
  if (!file || typeof file.arrayBuffer !== "function") return;
  if (file.size > MAX_SANDBOX_FILE_BYTES) return;
  const name = sanitizeSandboxFilename(file.name);
  _files.delete(name);
  _files.set(name, file);
  while (_files.size > MAX_SANDBOX_FILES) _files.delete(_files.keys().next().value);
}
export function sandboxFileNames() { return [..._files.keys()]; }

// Called when a reply finishes streaming with Code Interpreter on: its
// python blocks run once, as soon as they render.
export function queueAutoRun(text) { for (const code of extractPythonBlocks(text)) _autoRun.add(code); }
export function takeAutoRun(code) { if (_autoRun.has(code)) { _autoRun.delete(code); return true; } return false; }

function _spawn() {
  if (_worker) return _worker;
  if (!_workerUrl) _workerUrl = URL.createObjectURL(new Blob([buildWorkerSource()], { type: "text/javascript" }));
  _worker = new Worker(_workerUrl);
  _worker.onmessage = (e) => {
    const msg = e.data || {};
    const p = _pending.get(msg.id);
    if (!p) return;
    if (msg.type === "status") {
      if (msg.runStarted && !p.runTimer) {
        clearTimeout(p.timer);
        p.runTimer = setTimeout(() => _kill(msg.id, `Stopped: the code ran longer than ${Math.round(p.timeoutMs / 1000)} seconds.`), p.timeoutMs);
      }
      p.onStatus && p.onStatus(msg.status);
      return;
    }
    clearTimeout(p.timer); clearTimeout(p.runTimer);
    _pending.delete(msg.id);
    p.resolve(msg);
  };
  _worker.onerror = (e) => {
    for (const id of [..._pending.keys()]) _kill(id, "The Python sandbox crashed: " + (e && e.message ? e.message : "unknown error"));
  };
  return _worker;
}

function _kill(id, reason) {
  try { _worker && _worker.terminate(); } catch {}
  _worker = null;
  for (const [pid, pp] of [..._pending.entries()]) {
    clearTimeout(pp.timer); clearTimeout(pp.runTimer);
    _pending.delete(pid);
    pp.resolve({ type: "result", ok: false, error: pid === id ? reason : "The sandbox was restarted.", stdout: "", stderr: "", images: [], killed: true, durationMs: Date.now() - pp.startedAt });
  }
}

export async function runPython(code, { onStatus, timeoutMs = RUN_TIMEOUT_MS } = {}) {
  const w = _spawn();
  const files = [];
  for (const [name, f] of _files) {
    try { files.push({ name, bytes: await f.arrayBuffer() }); } catch {}
  }
  const id = ++_seq;
  return new Promise((resolve) => {
    const entry = { resolve, onStatus, timeoutMs, runTimer: null, startedAt: Date.now() };
    entry.timer = setTimeout(() => _kill(id, "Couldn't start Python in time — check your connection and try again."), LOAD_TIMEOUT_MS);
    _pending.set(id, entry);
    w.postMessage({ id, type: "run", code, files, extraPackages: extraPackagesFor(code) }, files.map((f) => f.bytes));
  });
}

// Run and record the outcome in the shared store (what the UI uses).
export async function runAndStore(code) {
  const cur = _results.get(code);
  if (cur && cur.state === "running") return cur.result;
  _results.set(code, { state: "running", status: "Starting…", result: cur ? cur.result : null });
  _emit();
  const result = await runPython(code, {
    onStatus: (status) => { const r = _results.get(code); if (r) { _results.set(code, { ...r, status }); _emit(); } },
  });
  const clean = {
    ok: !!result.ok,
    stdout: truncateOutput(result.stdout),
    stderr: truncateOutput(result.stderr),
    resultRepr: result.resultRepr ? truncateOutput(result.resultRepr, 5000) : null,
    table: result.table || null,
    images: Array.isArray(result.images) ? result.images.slice(0, 8) : [],
    error: result.error || null,
    durationMs: result.durationMs || 0,
  };
  _results.set(code, { state: "done", status: null, result: clean });
  _emit();
  return clean;
}

export function resetPythonSession() {
  _results.clear();
  _emit();
  if (_worker) _worker.postMessage({ id: ++_seq, type: "reset" });
}
